<template>
    <v-card class="heatmap-card elevation-1 rounded-lg mt-1 mx-3" :style="{ '--heatmap-accent': theme.accent, '--heatmap-highlight': theme.highlight }">
        <div class="heatmap-header">
            <div>
                <div class="heatmap-title secondary-font" :class="palette === 'green' ? 'heatmap-title-green' : 'custom-orange-text'">{{ title }}</div>
                <div class="heatmap-caption">{{ caption }}</div>
            </div>
            <div v-if="thresholds" class="heatmap-legend">
                <template v-for="(stop, index) in legendThresholds">
                    <span :key="`s-${index}`" class="heatmap-legend-swatch" :style="{ backgroundColor: toRgb(stop.color) }" />
                    <span :key="`l-${index}`" class="heatmap-legend-value">{{ stop.label }}</span>
                </template>
            </div>
            <div v-else class="heatmap-legend">
                <span>Less</span>
                <span v-for="step in [0.15, 0.4, 0.65, 0.85, 1]" :key="step" class="heatmap-legend-swatch" :style="{ backgroundColor: cellBackground(step) }" />
                <span>More</span>
            </div>
        </div>

        <v-progress-linear v-if="loading" indeterminate :color="theme.accent" class="mb-4" />
        <div v-else-if="rows.length" class="heatmap-scroll">
            <div class="heatmap-grid" :style="{ gridTemplateColumns }">
                <div class="heatmap-head heatmap-head-label">{{ labelHeader }}</div>
                <div v-for="period in periods" :key="`h-${period.key}`" class="heatmap-head">{{ period.label }}</div>
                <div class="heatmap-head">{{ totalHeader }}</div>

                <template v-for="row in rows">
                    <div
                        :key="`l-${row.id}`"
                        class="heatmap-label"
                        :class="{ 'heatmap-row-highlight heatmap-row-highlight-start': row.highlight, 'heatmap-clickable': clickable }"
                        @click="$emit('row-click', row)"
                    >
                        <slot name="label" :row="row" />
                    </div>
                    <div
                        v-for="period in periods"
                        :key="`c-${row.id}-${period.key}`"
                        class="heatmap-cell secondary-font"
                        :style="cellStyle(row, period.key)"
                    >
                        <template v-if="row.values[period.key] > 0">
                            {{ formatValue(row, row.values[period.key]) }}
                            <v-icon v-if="row.values[period.key] === rowMax(row)" x-small color="white" class="ml-1">{{ peakIcon }}</v-icon>
                        </template>
                        <span v-else class="heatmap-empty">–</span>
                    </div>
                    <div
                        :key="`t-${row.id}`"
                        class="heatmap-total secondary-font"
                        :class="{ 'heatmap-row-highlight heatmap-row-highlight-end': row.highlight }"
                    >
                        {{ formatValue(row, rowTotal(row)) }}
                    </div>
                </template>
            </div>
        </div>
        <div v-else class="text-center py-6 grey--text">No scoring data yet.</div>
    </v-card>
</template>

<script>
    // Periods 5+ (overtimes) are grouped into a single OT column
    const PERIODS = [
        { key: 1, label: '1st' },
        { key: 2, label: '2nd' },
        { key: 3, label: '3rd' },
        { key: 4, label: '4th' },
        { key: 'ot', label: 'OT' }
    ]

    // Each palette goes from a light tint to a deep shade; darkText is used on the lighter cells
    const PALETTES = {
        orange: {
            stops: [[255, 236, 224], [255, 180, 135], [255, 113, 51], [217, 72, 15]],
            darkText: '#7a2e0b',
            accent: '#ff7133',
            highlight: '#fff4ed'
        },
        green: {
            stops: [[226, 247, 235], [140, 216, 172], [38, 166, 98], [18, 110, 62]],
            darkText: '#0d4a2a',
            accent: '#26a662',
            highlight: '#eefaf3'
        }
    }

    // Shared heatmap card: one row per item, one column per period, each row shaded against its own best period.
    // Rows: { id, values: { 1, 2, 3, 4, ot }, decimals?: boolean, highlight?: boolean }
    export default {
        name: 'PeriodHeatmap',
        props: {
            title: {
                type: String,
                required: true
            },
            caption: {
                type: String,
                default: ''
            },
            labelHeader: {
                type: String,
                default: ''
            },
            totalHeader: {
                type: String,
                default: 'PTS'
            },
            rows: {
                type: Array,
                default: () => []
            },
            clickable: {
                type: Boolean,
                default: false
            },
            palette: {
                type: String,
                default: 'orange'
            },
            // Icon marking each row's highest period
            peakIcon: {
                type: String,
                default: 'mdi-fire'
            },
            loading: {
                type: Boolean,
                default: false
            },
            // Optional absolute color scale: [{ value, color: [r, g, b], label? }] sorted by value.
            // When set, cells are colored by their own value instead of relative to the row's best period.
            thresholds: {
                type: Array,
                default: null
            }
        },
        computed: {
            legendThresholds() {
                return (this.thresholds || []).filter(stop => stop.label)
            },
            theme() {
                return PALETTES[this.palette] || PALETTES.orange
            },
            periods() {
                // Only show the OT column when some row actually has overtime points
                const hasOvertime = this.rows.some(row => row.values.ot > 0)
                return hasOvertime ? PERIODS : PERIODS.filter(period => period.key !== 'ot')
            },
            gridTemplateColumns() {
                return `minmax(220px, 2fr) repeat(${this.periods.length}, minmax(64px, 1fr)) 72px`
            }
        },
        methods: {
            rowMax(row) {
                return Math.max(...Object.values(row.values))
            },
            rowTotal(row) {
                return Object.values(row.values).reduce((sum, points) => sum + points, 0)
            },
            formatValue(row, value) {
                return row.decimals ? value.toFixed(1) : value
            },
            cellBackground(ratio) {
                const stops = this.theme.stops
                const scaled = Math.min(Math.max(ratio, 0), 1) * (stops.length - 1)
                const index = Math.min(Math.floor(scaled), stops.length - 2)
                const t = scaled - index
                const [from, to] = [stops[index], stops[index + 1]]
                const channel = i => Math.round(from[i] + (to[i] - from[i]) * t)
                return `rgb(${channel(0)}, ${channel(1)}, ${channel(2)})`
            },
            toRgb(color) {
                return `rgb(${color[0]}, ${color[1]}, ${color[2]})`
            },
            thresholdColor(value) {
                const stops = this.thresholds
                if (value <= stops[0].value) return stops[0].color
                const upper = stops.findIndex(stop => value <= stop.value)
                if (upper === -1) return stops[stops.length - 1].color
                const [from, to] = [stops[upper - 1], stops[upper]]
                const t = (value - from.value) / (to.value - from.value)
                return from.color.map((channel, i) => Math.round(channel + (to.color[i] - channel) * t))
            },
            cellStyle(row, key) {
                if (this.thresholds) {
                    if (!(row.values[key] > 0)) return {}
                    const color = this.thresholdColor(row.values[key])
                    // Perceived brightness decides between dark and white text
                    const brightness = (color[0] * 299 + color[1] * 587 + color[2] * 114) / 1000
                    return {
                        backgroundColor: this.toRgb(color),
                        color: brightness > 170 ? '#3d3200' : 'white'
                    }
                }
                const max = this.rowMax(row)
                const ratio = max > 0 ? row.values[key] / max : 0
                if (ratio <= 0) return {}
                return {
                    backgroundColor: this.cellBackground(ratio),
                    color: ratio >= 0.6 ? 'white' : this.theme.darkText
                }
            }
        }
    }
</script>

<style scoped>
.heatmap-card {
    padding: 20px 24px;
}

.heatmap-header {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 20px;
}

.heatmap-title {
    font-size: 1.6rem;
    line-height: 1.2;
}

.heatmap-title-green {
    background-image: linear-gradient(90deg, #0f8a4f 0%, #26a662 60%, #6fd39a 100%);
    background-clip: text;
    -webkit-text-fill-color: transparent;
}

.heatmap-caption {
    margin-top: 4px;
    font-family: Ubuntu, Arial, sans-serif;
    font-size: 0.85rem;
    color: #8a8a8e;
}

.heatmap-legend {
    display: flex;
    align-items: center;
    gap: 3px;
    font-family: Ubuntu, Arial, sans-serif;
    font-size: 0.75rem;
    color: #8a8a8e;
}

.heatmap-legend span:first-child {
    margin-right: 4px;
}

.heatmap-legend span:last-child {
    margin-left: 4px;
}

.heatmap-legend-swatch {
    width: 20px;
    height: 10px;
    border-radius: 3px;
}

.heatmap-legend-value {
    margin: 0 6px 0 2px;
}

.heatmap-scroll {
    overflow-x: auto;
}

.heatmap-grid {
    display: grid;
    align-items: center;
    column-gap: 6px;
    row-gap: 6px;
    min-width: 560px;
}

.heatmap-head {
    font-family: Ubuntu, Arial, sans-serif;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
    text-align: center;
    color: #8a8a8e;
    padding-bottom: 4px;
}

.heatmap-head-label {
    text-align: left;
}

.heatmap-label {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    height: 42px;
    padding-right: 12px;
}

.heatmap-clickable {
    cursor: pointer;
}

.heatmap-clickable:hover {
    color: var(--heatmap-accent);
}

.heatmap-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 42px;
    border-radius: 8px;
    font-size: 1rem;
    background-color: #f6f6f7;
    transition: transform 0.15s;
}

.heatmap-cell:hover {
    transform: scale(1.04);
}

.heatmap-empty {
    color: #c4c4c8;
    font-weight: 400;
}

.heatmap-total {
    text-align: center;
    font-size: 1.1rem;
    color: #333;
    border-left: 2px solid #f0f0f2;
    height: 42px;
    line-height: 42px;
}

/* Summary row (e.g. team average): tinted label and total */
.heatmap-row-highlight {
    background-color: var(--heatmap-highlight);
}

.heatmap-row-highlight-start {
    border-radius: 8px;
    padding-left: 10px;
}

.heatmap-row-highlight-end {
    border-radius: 8px;
    border-left: none;
}
</style>
